/**
 * Carte géographique : une métrique par pays / région / département (choroplèthe).
 *
 * Porté de Metrix (`features/reports/widgets/MapWidget.tsx`), branché sur le socle
 * Orion (`chartStyle`, `fieldCatalog`, `useDrillDown`). Cliquer une zone descend
 * d'un niveau (pays → région → département).
 *
 * Les GeoJSON vivent dans `public/maps/` et sont chargés en `fetch` paresseux :
 * `france-departments.json` pèse 3,3 Mo, il ne doit JAMAIS entrer dans le bundle.
 * Chaque carte n'est chargée qu'une fois (`mapLoads`/`geoJsonCache`). Si le GeoJSON
 * est introuvable, le widget affiche « Carte indisponible ».
 *
 * `buildMapLayers` et `resolveMapName` sont des fonctions pures, testées sans DOM.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat'; // étend L avec L.heatLayer (carte de chaleur)
import 'leaflet.markercluster'; // étend L avec L.markerClusterGroup (regroupement de bulles)
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import type { WidgetConfig } from '../schemas';
import type { WidgetQueryResult } from '../useWidgetData';
import { useFieldCatalog, metricDef, metricFormat, metricLabel } from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { CHART_COLORS } from '../chartStyle';
import { useDrillDown } from '../useDrillDown';
import { DrillBreadcrumb } from '../DrillBreadcrumb';
import { getBasemap } from './basemaps';
import {
  aggregateClusterValue,
  buildMapLayers,
  bubbleLabelHtml,
  parseMapView,
  type ClusterChild,
  type MapView,
} from './mapLayers';
import { rampColor } from './mapColors';
import MapLegend from './MapLegend';

// Cadre + padding fournis par WidgetShell (WidgetRenderer) : ici, hauteur + flex.
const SHELL = 'h-full flex flex-col';
const DEFAULT_LIMIT = 50;

/** Cartes du dossier `public/maps/` : les seules valeurs proposées par le drawer. */
export const MAP_SCOPES = ['world', 'france', 'france-departments'] as const;

/**
 * Nom de carte à utiliser : le périmètre explicite du widget, sinon déduit de la
 * dimension géographique interrogée (pays → monde, région → France, département →
 * départements français).
 */
export function resolveMapName(display: Record<string, unknown>, dimension: string | undefined): string {
  const scope = display.map_scope;
  if (typeof scope === 'string' && scope) return scope;

  const dim = (dimension ?? '').toLowerCase();
  if (/departement|département|department/.test(dim)) return 'france-departments';
  if (/region|région/.test(dim)) return 'france';
  if (/city|ville/.test(dim)) return 'france';
  return 'world';
}

/**
 * Chargements de GeoJSON, mémorisés PAR PROMESSE (et non par résultat) : c'est la
 * promesse en vol qu'il faut partager. Mémoriser après le `await` laisserait deux
 * cartes d'une même page — ou le double effet de StrictMode — télécharger en
 * parallèle le même fichier, soit 6,6 Mo pour `france-departments.json`.
 * Même pattern que le cache de `fieldCatalog.ts`.
 */
const mapLoads = new Map<string, Promise<string[]>>();

interface GeoFeature {
  properties?: Record<string, unknown>;
}
interface GeoJson {
  features?: GeoFeature[];
}

/**
 * GeoJSON brut par nom de carte, mémorisé au même moment que le chargement
 * (`loadAndRegisterMap`) : le rendu Leaflet (`L.geoJSON`) en a besoin tel quel,
 * contrairement à `geoNames` (juste les libellés).
 */
const geoJsonCache = new Map<string, GeoJson>();

/** GeoJSON déjà chargé pour cette carte, ou `undefined` si pas encore prêt. */
export function getMapGeoJson(mapName: string): GeoJson | undefined {
  return geoJsonCache.get(mapName);
}

/**
 * Charge et enregistre un GeoJSON (une seule fois par nom). Jette si le fichier est
 * absent : l'appelant affiche alors « Carte indisponible ».
 */
export function ensureMapRegistered(mapName: string): Promise<string[]> {
  const inFlight = mapLoads.get(mapName);
  if (inFlight) return inFlight;

  const load = loadAndRegisterMap(mapName);
  // Un échec (réseau, 404) n'est pas mémorisé : la prochaine demande retentera.
  load.catch(() => mapLoads.delete(mapName));

  mapLoads.set(mapName, load);
  return load;
}

async function loadAndRegisterMap(mapName: string): Promise<string[]> {
  const res = await fetch(`/maps/${mapName}.json`);
  if (!res.ok) throw new Error(`GeoJSON introuvable : /maps/${mapName}.json`);
  const geoJson = (await res.json()) as GeoJson;

  // Les GeoJSON français portent le libellé sous `nom` ; on normalise vers `name`.
  for (const feature of geoJson.features ?? []) {
    const props = feature.properties;
    if (props && !props.name && props.nom) props.name = props.nom;
  }

  geoJsonCache.set(mapName, geoJson);
  return geoNamesOf(geoJson);
}

function geoNamesOf(geoJson: GeoJson | undefined): string[] {
  return (geoJson?.features ?? []).map((f) => String(f.properties?.name ?? '')).filter(Boolean);
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetQueryResult | null;
  loading?: boolean;
  error?: string;
  /** Mode édition du builder : affiche le contrôle Leaflet « Figer / Réinitialiser ». */
  editable?: boolean;
  /** Remonte la vue capturée au clic « Figer » (`null` = « Réinitialiser »). */
  onViewChange?: (view: MapView | null) => void;
}

export default function MapWidget({ config, data, loading, error, editable, onViewChange }: WidgetProps) {
  // Hooks avant tout early return (règle des hooks).
  const catalog = useFieldCatalog((data?.meta as { source_type?: string } | undefined)?.source_type);

  // Lue via ref (jamais en deps de l'effet Leaflet) : `onViewChange` n'est pas mémoïsée
  // côté ReportBuilder, l'ajouter aux deps remonterait la carte à chaque render.
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;

  // Confirmation transitoire au clic « Figer » : sans elle, l'utilisateur ne sait pas
  // que l'action a pris (la carte ne bouge pas — la vue figée = la vue courante).
  const [flashFrozen, setFlashFrozen] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerFlash = useCallback(() => {
    setFlashFrozen(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashFrozen(false), 1800);
  }, []);
  const triggerFlashRef = useRef(triggerFlash);
  triggerFlashRef.current = triggerFlash;
  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  const metric = config.metrics?.[0];
  const dimension = config.dimensions?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;
  const drillDimensions = (display.drill_dimensions as string[] | undefined) ?? [];
  const period = (data?.meta as { period?: { start: string; end: string } } | undefined)?.period;
  const basemapId = display.basemap as string | undefined;
  const cityLayer = (display.map_city_layer as string | undefined) ?? 'bubbles';
  const showValues = (display.map_show_values as boolean | undefined) ?? false;
  const clustering = (display.map_cluster as boolean | undefined) ?? true;
  // Mémoïsé sur `display.map_view` (et non `display`, recréé chaque render) : objet
  // stable en deps de l'effet Leaflet ci-dessous, sinon la carte remonterait à chaque render.
  const frozenView = useMemo(() => parseMapView(display), [display.map_view]);

  const mapName = resolveMapName(display, dimension);

  const [geoNames, setGeoNames] = useState<string[] | null>(null);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setGeoNames(null);
    setMapError(false);

    ensureMapRegistered(mapName)
      .then((names) => {
        if (!cancelled) setGeoNames(names);
      })
      .catch(() => {
        if (!cancelled) setMapError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [mapName]);

  const drill = useDrillDown({
    enabled: drillDimensions.length > 0 && !!config.data_source_id && !!metric && !!dimension && !!period,
    dimension: dimension ?? '',
    drillDimensions,
    dataSourceId: config.data_source_id ?? '',
    metric: metric ?? '',
    topN: (display.limit as number | undefined) ?? DEFAULT_LIMIT,
    period: period ?? { start: '', end: '' },
  });

  const layers = useMemo(
    () => buildMapLayers(config, data, catalog, geoNames ?? [], drill.drillItems),
    [config, data, catalog, geoNames, drill.drillItems],
  );

  // Coquille fine : le conteneur Leaflet n'est monté que dans l'état « succès »
  // (voir JSX ci-dessous) ; le ref reste `null` dans tous les autres états, l'effet
  // ci-dessous se contente alors de ne rien faire — pas de branche conditionnelle de
  // hook, juste un early-return à l'intérieur de l'effet (règle des hooks respectée).
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const basemap = getBasemap(basemapId);
    // `maxZoom` sur la carte elle-même (pas seulement sur la tuile) : `L.markerClusterGroup`
    // interroge `map.getMaxZoom()` pour calculer le zoom de dépliage d'un cluster, et jette
    // « Map has no maxZoom specified » si aucune couche à tuiles n'est présente (fond « Aplat »).
    const map = L.map(container, { attributionControl: true, maxZoom: basemap.maxZoom });
    // Vue initiale AVANT toute couche : Leaflet a besoin de `_pxBounds` (zoom/centre)
    // pour clipper une couche vectorielle (contours GeoJSON). Sans elle, ajouter un
    // polygone avant le premier fitBounds jette « Cannot read properties of undefined (min) ».
    map.setView([46.6, 2.4], 5);

    if (basemap.url) {
      L.tileLayer(basemap.url, { attribution: basemap.attribution, maxZoom: basemap.maxZoom }).addTo(map);
    }

    const format = metric ? metricFormat(catalog, metric) : 'number';
    const baseColor = CHART_COLORS[0];
    let fitted = false;

    if (layers.isScatter) {
      // Contours géographiques toujours dessinés sous les bulles : repère de lecture
      // (indispensable en fond « Aplat » sans tuiles, discret sur un fond à tuiles).
      const outlineGeoJson = getMapGeoJson(mapName);
      if (outlineGeoJson) {
        L.geoJSON(outlineGeoJson as never, {
          interactive: false,
          style: { fillColor: '#F4F5FA', fillOpacity: 0.35, color: '#D4D7E2', weight: 1 },
        }).addTo(map);
      }

      const bounds: [number, number][] = layers.bubbles.map((b) => [b.lat, b.lng]);

      // Vue (fit/figée) posée AVANT la création des couches bulles/cluster : le
      // regroupement `L.markerClusterGroup` calcule ses clusters au zoom courant de la
      // carte au moment de `addTo(map)`. La poser après (comme avant PR-5) figeait les
      // clusters au zoom initial très dézoomé (5, France entière) au lieu du zoom cadré.
      if (frozenView) {
        map.setView(frozenView.center, frozenView.zoom);
      } else if (bounds.length > 0) {
        map.fitBounds(bounds, { maxZoom: 12 });
        fitted = true;
      }

      if (cityLayer === 'heat') {
        // Carte de chaleur : points villes pondérés par la métrique. Plancher d'intensité
        // à 0,3 (et non 0) : sinon les villes à faible valeur tombent sous le seuil de
        // transparence du gradient et deviennent invisibles. `minOpacity` garantit une
        // opacité minimale, rayon/blur élargis pour une lecture à l'échelle nationale.
        const points: L.HeatLatLngTuple[] = layers.bubbles.map((b) => [
          b.lat,
          b.lng,
          layers.max > 0 ? 0.3 + 0.7 * (b.value / layers.max) : 0.6,
        ]);
        L.heatLayer(points, {
          radius: 35,
          blur: 22,
          minOpacity: 0.45,
          maxZoom: 12,
          gradient: { 0.1: '#C7D2FE', 0.35: '#818CF8', 0.6: '#6366F1', 0.8: '#DB2777', 1.0: '#BE123C' },
        }).addTo(map);
      } else {
        // Regroupement au dézoom (défaut ON) : les bulles sont ajoutées à un groupe
        // markercluster plutôt qu'individuellement à la carte. Le cluster affiche la
        // valeur agrégée de ses enfants (Σvalue additive, ou Σnum/Σdénom pour un ratio —
        // `aggregateClusterValue`), pas un simple compteur.
        const isRatio = metricDef(catalog, metric)?.type === 'ratio';
        const clusterGroup = clustering
          ? L.markerClusterGroup({
              maxClusterRadius: 60,
              showCoverageOnHover: false,
              iconCreateFunction: (cluster) => {
                const children = cluster
                  .getAllChildMarkers()
                  .map((m) => (m as unknown as { __agg?: ClusterChild }).__agg)
                  .filter((c): c is ClusterChild => c != null);
                const agg = aggregateClusterValue(children, isRatio);
                const label = formatMetricValue(agg, format);
                const r = 18; // taille fixe lisible ; le texte porte l'info
                return L.divIcon({
                  html: `<div style="display:flex;align-items:center;justify-content:center;width:${2 * r}px;height:${2 * r}px;border-radius:50%;background:${baseColor};color:#fff;font-size:11px;font-weight:600;box-shadow:0 1px 4px rgba(0,0,0,.3)">${label}</div>`,
                  className: '',
                  iconSize: [2 * r, 2 * r],
                });
              },
            })
          : null;

        for (const bubble of layers.bubbles) {
          const ratio = layers.max > 0 ? bubble.value / layers.max : 0;
          const radius = 8 + ratio * 32; // borné [8, 40], comme l'ancien symbolSize ECharts
          // Couleur = 2e métrique (intensité) quand elle est configurée et disponible
          // (niveau carte uniquement — pas en drill) ; sinon teinte marque unie.
          const fill =
            layers.colorMax && layers.colorMax > 0 && bubble.colorValue != null
              ? rampColor(bubble.colorValue / layers.colorMax)
              : baseColor;
          const marker = L.circleMarker([bubble.lat, bubble.lng], {
            radius,
            color: fill,
            fillColor: fill,
            fillOpacity: 0.7,
            weight: 1,
          });
          // Infos d'agrégation portées par le marker : lues par `iconCreateFunction`
          // quand ce marker se retrouve regroupé dans un cluster.
          (marker as unknown as { __agg: ClusterChild }).__agg = {
            value: bubble.value,
            sizeNum: bubble.sizeNum,
            sizeDenom: bubble.sizeDenom,
          };
          const labelMetrics = [
            {
              label: metric ? metricLabel(catalog, metric) : '',
              value: bubble.value,
              format: (v: number) => formatMetricValue(v, format),
            },
          ];
          if (layers.colorMetric && bubble.colorValue != null) {
            const cf = metricFormat(catalog, layers.colorMetric);
            labelMetrics.push({
              label: metricLabel(catalog, layers.colorMetric),
              value: bubble.colorValue,
              format: (v: number) => formatMetricValue(v, cf),
            });
          }
          marker.bindTooltip(
            bubbleLabelHtml(bubble.name, labelMetrics, (v) => formatMetricValue(v, format)),
            showValues ? { permanent: true, direction: 'top', offset: [0, -6] } : { direction: 'top' },
          );
          if (drill.canDrillDown) {
            marker.on('click', () => void drill.handleDrillDown(bubble.name));
          }
          if (clusterGroup) {
            clusterGroup.addLayer(marker);
          } else {
            marker.addTo(map);
          }
        }

        if (clusterGroup) {
          clusterGroup.addTo(map);
        }
      }
    } else if (layers.regions.length > 0) {
      const geoJson = getMapGeoJson(mapName);
      if (geoJson) {
        const valueByName = new Map(layers.regions.map((r) => [r.name, r.value]));
        const geoLayer = L.geoJSON(geoJson as never, {
          style: (feature) => {
            const name = String(feature?.properties?.name ?? '');
            const value = valueByName.get(name) ?? 0;
            const ratio = layers.max > 0 ? value / layers.max : 0;
            return {
              fillColor: ratio > 0 ? baseColor : '#F4F5FA',
              fillOpacity: ratio > 0 ? 0.15 + ratio * 0.65 : 0.3,
              color: '#E2E4ED',
              weight: 1,
            };
          },
          onEachFeature: (feature, layer) => {
            if (!drill.canDrillDown) return;
            const name = String(feature?.properties?.name ?? '');
            layer.on('click', () => void drill.handleDrillDown(name));
          },
        });
        geoLayer.addTo(map);

        // Bulle au centre de chaque région valorisée : rayon ∝ m2 (couleur), niveau
        // carte uniquement (colorMax absent en drill ou sans 2e métrique configurée).
        if (layers.colorMax && layers.colorMax > 0 && layers.colorMetric) {
          const colorByName = new Map(layers.regions.map((r) => [r.name, r.colorValue]));
          geoLayer.eachLayer((lyr) => {
            const feat = (lyr as { feature?: { properties?: Record<string, unknown> } }).feature;
            const name = String(feat?.properties?.name ?? '');
            const cv = colorByName.get(name);
            if (cv == null) return;
            const center = (lyr as unknown as { getBounds(): L.LatLngBounds }).getBounds().getCenter();
            const ratio = cv / layers.colorMax!;
            const labelMetrics = [
              {
                label: metric ? metricLabel(catalog, metric) : '',
                value: valueByName.get(name) ?? 0,
                format: (v: number) => formatMetricValue(v, format),
              },
            ];
            const cf = metricFormat(catalog, layers.colorMetric!);
            labelMetrics.push({
              label: metricLabel(catalog, layers.colorMetric!),
              value: cv,
              format: (v: number) => formatMetricValue(v, cf),
            });
            L.circleMarker(center, {
              radius: 6 + ratio * 18,
              color: CHART_COLORS[0],
              fillColor: CHART_COLORS[0],
              fillOpacity: 0.75,
              weight: 1,
              interactive: false,
            })
              .bindTooltip(
                bubbleLabelHtml(name, labelMetrics, (v) => formatMetricValue(v, format)),
                showValues ? { permanent: true, direction: 'top', offset: [0, -6] } : { direction: 'top' },
              )
              .addTo(map);
          });
        }

        const bounds = geoLayer.getBounds();
        if (!frozenView && bounds.isValid()) {
          map.fitBounds(bounds);
          fitted = true;
        }
      }
    }

    // Contrôle « Figer / Réinitialiser » : uniquement en mode édition (calqué sur
    // `rich_text` — l'affordance d'édition vit dans le widget, pas dans un panneau).
    if (editable) {
      const FreezeControl = L.Control.extend({
        // Haut-gauche (sous le zoom) : le haut-droite est occupé par la barre d'outils
        // du widget en mode édition (verrouiller/supprimer…) — un contrôle Leaflet y
        // passerait par-dessus (z-index Leaflet > z-index de la barre).
        options: { position: 'topleft' },
        onAdd() {
          const box = L.DomUtil.create('div', 'leaflet-bar');
          box.style.background = '#fff';
          const freeze = L.DomUtil.create('a', '', box);
          freeze.href = '#';
          freeze.textContent = '⊡';
          // État actif quand une vue est déjà figée : feedback persistant (« c'est figé »)
          // en plus de la confirmation transitoire au clic.
          if (frozenView) {
            freeze.style.background = '#6366F1';
            freeze.style.color = '#fff';
            freeze.title = 'Vue d’ouverture figée — cliquer pour recadrer';
            freeze.setAttribute('aria-label', 'Vue figée, cliquer pour recadrer');
          } else {
            freeze.title = 'Figer la vue actuelle comme vue d’ouverture';
            freeze.setAttribute('aria-label', 'Figer la vue actuelle');
          }
          const reset = L.DomUtil.create('a', '', box);
          reset.href = '#';
          reset.title = 'Réinitialiser la vue';
          reset.textContent = '↺';
          reset.setAttribute('aria-label', 'Réinitialiser la vue');
          L.DomEvent.disableClickPropagation(box);
          L.DomEvent.on(freeze, 'click', (e) => {
            L.DomEvent.preventDefault(e);
            const c = map.getCenter();
            onViewChangeRef.current?.({ zoom: map.getZoom(), center: [c.lat, c.lng] });
            triggerFlashRef.current();
          });
          L.DomEvent.on(reset, 'click', (e) => {
            L.DomEvent.preventDefault(e);
            onViewChangeRef.current?.(null);
          });
          return box;
        },
      });
      new FreezeControl().addTo(map);
    }

    // Vue figée (bouton « Figer la vue ») : prime sur le fit auto. Sinon, repli France
    // si rien n'a été cadré (données vides, GeoJSON absent…).
    if (frozenView) {
      map.setView(frozenView.center, frozenView.zoom);
    } else if (!fitted) {
      map.setView([46.6, 2.4], 5);
    }

    return () => {
      map.remove();
    };
  }, [
    layers,
    basemapId,
    cityLayer,
    mapName,
    catalog,
    metric,
    drill.canDrillDown,
    drill.handleDrillDown,
    frozenView,
    editable,
    showValues,
    clustering,
  ]);

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  // Un échec de drill-down (400, timeout…) s'affiche dans l'état d'erreur du widget.
  const shownError = error ?? drill.error;

  if (shownError) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-red-600">{shownError}</span>
      </div>
    );
  }

  if (!data || !config.data_source_id || !metric || !dimension) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  // GeoJSON absent ou illisible : pas de choroplèthe possible pour ce nom de carte.
  if (mapError) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">Carte indisponible</span>
      </div>
    );
  }

  if (!geoNames) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">Chargement de la carte…</span>
      </div>
    );
  }

  const hasLayers = layers.bubbles.length > 0 || layers.regions.length > 0;

  return (
    <div className={SHELL}>
      <DrillBreadcrumb drillPath={drill.drillPath} catalog={catalog} onDrillTo={drill.drillTo} />
      {drill.isLoading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-[#8B8FA7]">Chargement…</div>
      ) : !hasLayers ? (
        <div className="flex flex-1 items-center justify-center text-sm italic text-[#8B8FA7]">Aucune donnée</div>
      ) : (
        <div className="relative flex-1" style={{ minHeight: 260 }}>
          <div ref={mapContainerRef} className="absolute inset-0" />
          {flashFrozen && (
            <div className="pointer-events-none absolute left-1/2 top-2 z-[600] -translate-x-1/2 rounded-md bg-[#1B2559] px-2.5 py-1 text-xs font-medium text-white shadow">
              Vue d’ouverture figée ✓
            </div>
          )}
          {layers.sizeMetric && (
            <MapLegend
              sizeLabel={metricLabel(catalog, layers.sizeMetric)}
              sizeMax={layers.max}
              sizeFormat={metricFormat(catalog, layers.sizeMetric)}
              colorLabel={layers.colorMetric ? metricLabel(catalog, layers.colorMetric) : undefined}
              colorMin={0}
              colorMax={layers.colorMax}
              colorFormat={layers.colorMetric ? metricFormat(catalog, layers.colorMetric) : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}
